import { toast } from '@cuc/ui/kit';
import axios, { type AxiosError, type CreateAxiosDefaults } from 'axios';

import { getAccessToken } from './tokenProvider';

const SIGN_IN_RELOAD_KEY = 'workflows-player-sign-in-reload-at';
const SIGN_IN_RELOAD_INTERVAL_MS = 60_000;
const TOAST_REPEAT_MS = 10_000;

/** A token the API keeps rejecting would reload the page forever, so a 401 reloads at most once a minute. */
const reserveSignInReload = (): boolean => {
  try {
    const lastReloadAt = Number(sessionStorage.getItem(SIGN_IN_RELOAD_KEY));

    if (Date.now() - lastReloadAt < SIGN_IN_RELOAD_INTERVAL_MS) {
      return false;
    }

    sessionStorage.setItem(SIGN_IN_RELOAD_KEY, String(Date.now()));

    return true;
  } catch {
    return false;
  }
};

// The kit toast has no id, so one toast per kind while TanStack Query retries the same failed read.
const lastToastAt = new Map<string, number>();
const toastOnce = (key: string, text: string) => {
  const now = Date.now();

  if (now - (lastToastAt.get(key) ?? 0) < TOAST_REPEAT_MS) return;
  lastToastAt.set(key, now);
  toast({ tone: 'critical', text });
};

/** Thrown before the token is attached when a service has no base URL: the request would go to the host origin. */
export class MissingBaseUrlError extends Error {
  constructor() {
    super('The API base URL is not configured');
    this.name = 'MissingBaseUrlError';
  }
}

export function createApiClient(serviceConfig: CreateAxiosDefaults, skipInterceptorResponse = false) {
  const apiClient = axios.create({
    ...serviceConfig,
    headers: {
      'Content-Type': 'application/json',
      ...serviceConfig.headers,
    },
  });

  let reportedMissingBaseUrl = false;

  apiClient.interceptors.request.use(async (config) => {
    if (!config.baseURL?.trim()) {
      if (!reportedMissingBaseUrl) {
        reportedMissingBaseUrl = true;
        console.error('API request blocked: the service has no base URL.');
      }

      throw new MissingBaseUrlError();
    }

    const token = await getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });

  if (skipInterceptorResponse) {
    return apiClient;
  }

  apiClient.interceptors.response.use(
    (response) => response.data,
    (error: AxiosError) => {
      if (error.response) {
        const { status } = error.response;

        switch (status) {
          case 401: {
            const reloading = reserveSignInReload();

            toastOnce('unauthorized', reloading ? 'Your session has ended. Signing you in again...' : 'Unauthorized');

            if (reloading) {
              window.location.reload();
            }
            break;
          }
          case 400:
          case 404:
            // The page handles these; a toast on top would repeat it.
            break;
          case 500:
            toastOnce('server-error', 'Internal server error');
            break;
          default:
            toastOnce('unexpected-error', 'Unexpected error');
            // The status only: a response body may echo request data.
            console.error('Unexpected error:', status);
        }
      }

      return Promise.reject(error);
    },
  );

  return apiClient;
}
